<template>
  <div class="row mb-2 p-4 p-lg-1 g-0">
    <div
      v-if="is_admin"
      class="col-12 col-lg-1 px-1 py-1 py-lg-0 text-lg-center"
    >
      <p class="mb-0 d-lg-none">Invoice ID</p>
      <span
        class="alert alert-subtle-warning text-center admin-extra-info px-2 py-1 d-inline-block"
        >{{ invoice.id }}</span
      >
    </div>
    <div
      v-if="is_admin"
      class="col-12 col-lg-1 px-1 py-1 py-lg-0 text-lg-center"
    >
      <p class="mb-0 d-lg-none">Admin actions</p>
      <button
        v-if="invoice.invoice_type === 'fee'"
        class="btn btn-outline-warning btn-sm"
        @click="regenerateInvoice"
        :disabled="isLoading"
        :title="t('invoice.regenerate')"
      >
        <template v-if="isLoading">
          <font-awesome-icon class="px-1" icon="spinner" spin />
        </template>
        <template v-else>
          <font-awesome-icon class="px-1" icon="recycle" />
        </template>
      </button>
      <button
        v-if="invoice.invoice_type === 'fee' && invoice.is_fee_only"
        class="btn btn-outline-danger btn-sm ms-1"
        @click="cancelFeeOnlyInvoice"
        :disabled="isLoading"
        :title="t('invoice.cancel_fee_only')"
      >
        <template v-if="isLoading">
          <font-awesome-icon class="px-1" icon="spinner" spin />
        </template>
        <template v-else>
          <font-awesome-icon class="px-1" icon="xmark" />
        </template>
      </button>
      <button
        v-if="canSendInvoiceToMerit"
        class="btn btn-outline-warning btn-sm ms-1"
        @click="sendInvoiceToMerit"
        :disabled="isLoading"
        :title="t('invoice.send_to_merit')"
      >
        <template v-if="isLoading">
          <font-awesome-icon class="px-1" icon="spinner" spin />
        </template>
        <template v-else>
          <font-awesome-icon class="px-1" icon="paper-plane" />
        </template>
      </button>
      <span v-if="!hasAdminAction" class="text-muted">-</span>
    </div>
    <div v-if="showCompanyColumn" class="col-6 col-lg-2 px-1 py-1 py-lg-0">
      <p class="mb-0 d-lg-none">{{ $t("common.company") }}</p>
      <p class="p-0">{{ companyName }}</p>
    </div>
    <div class="col-6 col-lg px-1 py-1 py-lg-0">
      <p class="mb-0 d-lg-none">{{ $t("invoice.number") }}</p>
      <p class="p-0 fw-bold">{{ invoice.invoice_number }}</p>
    </div>
    <div class="col-6 col-lg-1 px-1 py-1 py-lg-0 text-lg-center">
      <p class="mb-0 d-lg-none">{{ $t("invoice.status_label") }}</p>
      <span :class="statusBadgeClass">{{ invoiceStatus }}</span>
    </div>
    <div class="col-6 col-lg-2 px-1 py-1 py-lg-0 text-lg-center">
      <p class="mb-0 d-lg-none">{{ $t("invoice.requested_date") }}</p>
      <p class="p-0">{{ formatRequestDate }}</p>
    </div>
    <div class="col-6 col-lg-1 px-1 py-1 py-lg-0 text-lg-end">
      <p class="mb-0 d-lg-none">{{ $t("invoice.amount") }}</p>
      <p class="p-0">{{ totalPayout }}</p>
    </div>
    <div class="col-6 col-lg-2 px-1 py-1 py-lg-0 text-lg-center">
      <p class="mb-0 d-lg-none">{{ $t("document.download") }}</p>
      <button
        class="btn btn-outline-primary btn-sm"
        @click="startDownload"
        :disabled="isLoading"
      >
        <template v-if="isLoading">
          <font-awesome-icon class="px-1" icon="spinner" spin />
        </template>
        <template v-else>
          <font-awesome-icon class="px-1" icon="download" />
        </template>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  formatDateTimeIntoDate,
  formatMoneyWithCurrency,
} from "@/composables/formatUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { Invoice } from "@/types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    invoice: Invoice;
    is_admin?: boolean;
    showCompanyColumn?: boolean;
    companyName?: string;
  }>(),
  {
    is_admin: false,
    showCompanyColumn: false,
    companyName: "",
  }
);

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const fileDownload = useFileDownload();
const isLoading = ref(false);
const totalPayout = computed(() => {
  return formatMoneyWithCurrency(
    props.invoice.display_amount,
    props.invoice.currency
  );
});

const formatRequestDate = computed(() => {
  if (props.invoice.requested_at) {
    return formatDateTimeIntoDate(props.invoice.requested_at);
  } else if (props.invoice.created_at) {
    return formatDateTimeIntoDate(props.invoice.created_at);
  }
  return "-";
});

const invoiceStatus = computed(() => {
  if (props.invoice.paid_at) {
    return t("invoice.status.paid");
  } else if (props.invoice.canceled_at) {
    return t("invoice.status.canceled");
  } else if (props.invoice.requested_at) {
    return t("invoice.status.requested");
  } else {
    return t("invoice.status.drafting");
  }
});

const statusBadgeClass = computed(() => {
  if (props.invoice.paid_at) {
    return "badge bg-success";
  } else if (props.invoice.canceled_at) {
    return "badge bg-danger";
  } else if (props.invoice.requested_at) {
    return "badge bg-primary";
  } else {
    return "badge bg-secondary";
  }
});

const canSendInvoiceToMerit = computed(() => {
  const canBeRegisteredInMerit = ["fee", "flat_fee", "expense"].includes(
    props.invoice.invoice_type
  );
  const hasMeritMetadata =
    props.invoice.merit_metadata &&
    Object.keys(props.invoice.merit_metadata).length > 0;
  return canBeRegisteredInMerit && !hasMeritMetadata;
});

const hasAdminAction = computed(() => {
  return props.invoice.invoice_type === "fee" || canSendInvoiceToMerit.value;
});

const cancelFeeOnlyInvoice = async () => {
  if (confirm(t("invoice.cancel_fee_only_confirmation"))) {
    isLoading.value = true;
    try {
      const response = await goSolidApi.post("/api/admin/cancel-invoices", {
        invoice_ids: [props.invoice.id],
      });
      if (response.status === 200) {
        window.location.reload();
      } else {
        throw new Error(t("invoice.error_downloading"));
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    } finally {
      isLoading.value = false;
    }
  }
};

const regenerateInvoice = async () => {
  if (confirm(t("invoice.regenerate_confirmation"))) {
    isLoading.value = true;
    try {
      const response = await goSolidApi.post(
        `/api/admin/fee_invoice/${props.invoice.id}/regenerate`
      );

      if (response.status === 200) {
        window.location.reload();
      } else {
        throw new Error(t("invoice.error_downloading"));
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    } finally {
      isLoading.value = false;
    }
  }
};

const sendInvoiceToMerit = async () => {
  if (confirm(t("invoice.send_to_merit_confirmation"))) {
    isLoading.value = true;
    try {
      const invoiceTypePrefix =
        props.invoice.invoice_type === "expense" ? "expense" : "fee";
      const response = await goSolidApi.post(
        `/api/admin/${invoiceTypePrefix}_invoice/${props.invoice.id}/send_to_merit`
      );
      const result = response.data?.result;
      if (typeof result === "string" && result === "already_registered") {
        globalSuccessHandler("Success", t("invoice.already_in_merit"));
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    } finally {
      isLoading.value = false;
    }
  }
};

const startDownload = async () => {
  isLoading.value = true;
  try {
    await fileDownload.downloadInvoice(props.invoice.id);
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
.badge {
  font-size: 0.75rem;
}
</style>
